import { Box, IconButton, Typography } from "@mui/material";
import { Drawer, DrawerContent } from "Components/Analyses/_Shared/Drawer";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { HStack } from "Components/_Shared/Core/Stack";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import type { FC, FormEvent, ReactNode } from "react";
import { FormDrawerUnsavedBadge } from "./FormDrawerUnsavedBadge";

export type FormDrawerSize = "sm" | "md" | "lg";

// theme spacing units (480 / 720 / 900 px); the Drawer itself caps the width to the viewport on smaller screens
const PAPER_WIDTH: Record<FormDrawerSize, number> = {
  sm: 120,
  md: 180,
  lg: 225,
};

type Props = {
  readonly open: boolean;
  readonly size?: FormDrawerSize;
  readonly title: string;
  readonly submitLabel: string;
  readonly onClose: () => void;
  readonly onSubmit: () => void;
  readonly isSubmitting?: boolean;
  readonly isSubmitDisabled?: boolean;
  /** Unsaved changes across the whole drawer, shown next to the buttons. */
  readonly unsavedCount?: number;
  readonly navigation?: ReactNode;
  readonly children?: ReactNode;
};

export const FormDrawer: FC<Props> = ({
  open,
  size = "md",
  title,
  submitLabel,
  onClose,
  onSubmit,
  isSubmitting = false,
  isSubmitDisabled = false,
  unsavedCount = 0,
  navigation,
  children,
}) => {
  const { t } = useResource();

  const requestClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    // React bubbles events through portals, so a form higher up in the component tree would submit as well
    event.stopPropagation();
    if (isSubmitting || isSubmitDisabled) {
      return;
    }
    onSubmit();
  };

  return (
    <Drawer anchor="right" open={open} paperWidth={PAPER_WIDTH[size]} onClose={requestClose}>
      <Flex component="form" flexDirection="column" height="100%" onSubmit={handleSubmit}>
        <Flex flexShrink={0} justifyContent="space-between" alignItems="center" p={Spacing.XXL}>
          <Typography variant="h2">{title}</Typography>

          <IconButton aria-label={t(Resources.Common.Close)} disabled={isSubmitting} onClick={requestClose}>
            <CloseIcon />
          </IconButton>
        </Flex>

        {/* only the header and the footer stay put, the navigation scrolls together with the content */}
        <DrawerContent
          display="flex"
          flexDirection="column"
          flex={1}
          minHeight={0}
          bgcolor="grey.50"
          sx={{ mr: 0, "&::-webkit-scrollbar-track": { bgcolor: "grey.50" } }}
        >
          {navigation && (
            <Box flexShrink={0} px={Spacing.XXL} pb={Spacing.XXL} bgcolor="common.white">
              {navigation}
            </Box>
          )}

          {/* grows to the bottom of the drawer, so the grey work area doesn't end halfway when the content is short */}
          <Box flex="1 0 auto" p={Spacing.XXL} borderTop={1} borderColor="divider">
            {children}
          </Box>
        </DrawerContent>

        <Flex
          flexShrink={0}
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
          gap={Spacing.XL}
          p={Spacing.XXL}
          borderTop={1}
          borderColor="divider"
        >
          {unsavedCount > 0 && <FormDrawerUnsavedBadge count={unsavedCount} />}

          <HStack spacing={Spacing.XL} flexShrink={0} ml="auto">
            <Button color="secondary" disabled={isSubmitting} onClick={requestClose}>
              {t(Resources.Common.Close)}
            </Button>
            <Button type="submit" loading={isSubmitting} disabled={isSubmitDisabled}>
              {submitLabel}
            </Button>
          </HStack>
        </Flex>
      </Flex>
    </Drawer>
  );
};
